import { useState, useEffect } from "react";
import HLAMatchScore from "./HLAMatchScore";
import SABTestScore from "./SABTestScores";
import { EpletClientService } from "../../../utils/epletService";

// ---- Color-coded badge for molecular eplet risk ----

export const EpletRiskBadge = ({ riskTier, epletScore }) => {
  if (!riskTier) return null;
  let bg = "bg-green-100 text-green-800 border-green-300";
  let label = "Eplet: Low Risk";
  if (riskTier === "INTERMEDIATE") {
    bg = "bg-yellow-100 text-yellow-800 border-yellow-300";
    label = "Eplet: Moderate Load";
  } else if (riskTier === "HIGH" || riskTier === "CONTRAINDICATED") {
    bg = "bg-red-100 text-red-800 border-red-300";
    label = riskTier === "CONTRAINDICATED" ? "Eplet: Preformed DSA" : "Eplet: High Risk";
  }
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${bg} inline-flex items-center gap-1`}
      title={`EpRegistry Compatibility: ${epletScore !== undefined ? epletScore + '%' : ''}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"></span>
      {label}
    </span>
  );
};

// ---- Color-coded badge for a single score ----

const getScoreColor = (score, isIncompatible = false) => {
  if (isIncompatible) return { bg: "bg-red-100", text: "text-red-800", ring: "ring-red-500", label: "Unfit - Incompatible" };
  if (score === null || score === undefined) return { bg: "bg-gray-200", text: "text-gray-500", ring: "ring-gray-300", label: "Analyzing..." };
  if (score >= 80) return { bg: "bg-green-100", text: "text-green-800", ring: "ring-green-400", label: "Excellent Match" };
  if (score >= 60) return { bg: "bg-yellow-100", text: "text-yellow-800", ring: "ring-yellow-400", label: "Good Match" };
  if (score >= 40) return { bg: "bg-orange-100", text: "text-orange-800", ring: "ring-orange-400", label: "Fair - Needs Review" };
  return { bg: "bg-red-100", text: "text-red-800", ring: "ring-red-400", label: "High Risk" };
};

const scoreToPercent = (s) => {
  if (s === null || s === undefined) return null;
  const n = parseFloat(s);
  return isNaN(n) ? null : Math.round(n);
};

export const SuccessBadge = ({ score, size = "md", showLabel = true, isIncompatible = false }) => {
  const pct = scoreToPercent(score);
  const colors = getScoreColor(pct, isIncompatible);

  const sizeClasses = size === "sm"
    ? "w-10 h-10 text-xs"
    : "w-14 h-14 text-sm";

  const ringWidth = size === "sm" ? "ring-2" : "ring-2";

  return (
    <div className="flex items-center gap-2" title={colors.label}>
      <div
        className={`${sizeClasses} ${ringWidth} ${colors.ring} rounded-full flex items-center justify-center font-bold ${colors.text} ${colors.bg} flex-shrink-0`}
      >
        {pct !== null ? `${pct}` : "—"}
      </div>
      {showLabel && (
        <div className="flex flex-col">
          <span className={`text-xs font-semibold ${colors.text}`}>
            {pct !== null ? `${pct}%` : "—"}
          </span>
          <span className="text-[10px] text-gray-500 leading-tight">
            {colors.label}
          </span>
        </div>
      )}
    </div>
  );
};

// ---- Score indicator bar (mini progress bar) ----

export const ScoreBar = ({ score, className = "", isIncompatible = false }) => {
  const pct = scoreToPercent(score);
  if (pct === null && !isIncompatible) return null;
  if (isIncompatible) {
    return (
      <div className={`w-full h-1.5 bg-gray-200 rounded-full overflow-hidden ${className}`}>
        <div className="h-full rounded-full bg-red-500" style={{ width: "100%" }} />
      </div>
    );
  }
  const barColor =
    pct >= 80 ? "bg-green-500" :
    pct >= 60 ? "bg-yellow-500" :
    pct >= 40 ? "bg-orange-500" :
    "bg-red-500";

  return (
    <div className={`w-full h-1.5 bg-gray-200 rounded-full overflow-hidden ${className}`}>
      <div
        className={`h-full rounded-full transition-all duration-700 ${barColor}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
};

// ---- Hook: compute HLA + SAB scores for a single recipient-donor pair ----

export const useMatchScore = (recipientId, donorId, providedClinicalData = null) => {
  const [hlaScore, setHlaScore] = useState(null);
  const [sabScore, setSabScore] = useState(null);
  const [epletScore, setEpletScore] = useState(null);
  const [epletRiskTier, setEpletRiskTier] = useState(null);
  const [epletAnalysis, setEpletAnalysis] = useState(null);
  const [compositeScore, setCompositeScore] = useState(null);
  const [isIncompatible, setIsIncompatible] = useState(false);
  const [incompatibleReason, setIncompatibleReason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!recipientId || !donorId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const token = localStorage.getItem("token");

    const compute = async () => {
      setLoading(true);
      setError(null);
      try {
        const [hlaResult, sabResult, epletResult] = await Promise.all([
          HLAMatchScore(recipientId, donorId, providedClinicalData),
          SABTestScore(recipientId, donorId, providedClinicalData),
          EpletClientService.fetchAnalysis(recipientId, donorId, token, providedClinicalData),
        ]);

        if (cancelled) return;

        const hlaPct = parseFloat(hlaResult);
        const hlaVal = isNaN(hlaPct) ? null : hlaPct;

        let sabVal = null;
        if (sabResult && sabResult.score) {
          const parsed = parseFloat(sabResult.score);
          sabVal = isNaN(parsed) ? null : parsed;
        }

        let epletVal = null;
        let epletTier = null;
        if (epletResult && epletResult.epletScore !== undefined) {
          epletVal = epletResult.epletScore;
          epletTier = epletResult.riskTier;
          setEpletScore(epletVal);
          setEpletRiskTier(epletTier);
          setEpletAnalysis(epletResult);
        }

        const sabIncompatible = sabResult?.isIncompatible === true;
        const epletIncompatible = epletResult?.hasHighMfiEpletDsa === true;
        const pairIncompatible = sabIncompatible || epletIncompatible;
        const incompatibleDetail =
          sabResult?.incompatibleReason ||
          (epletIncompatible ? "Preformed Eplet DSA > 10,000 MFI (Unfit)" : null);

        setIsIncompatible(pairIncompatible);
        setIncompatibleReason(incompatibleDetail);
        setHlaScore(hlaVal);
        setSabScore(sabVal);

        // Three-Pillar Molecular Composite Scoring:
        // 20% Antigen HLA + 35% Molecular Eplet + 45% SAB Luminex DSA
        if (pairIncompatible) {
          setCompositeScore(0);
        } else if (hlaVal !== null && epletVal !== null && sabVal !== null) {
          setCompositeScore(hlaVal * 0.2 + epletVal * 0.35 + sabVal * 0.45);
        } else if (hlaVal !== null && sabVal !== null) {
          setCompositeScore(hlaVal * 0.4 + sabVal * 0.6);
        } else if (hlaVal !== null) {
          setCompositeScore(hlaVal);
        } else if (sabVal !== null) {
          setCompositeScore(sabVal);
        } else {
          setCompositeScore(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("MatchSuccessScore error:", err);
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    compute();
    return () => {
      cancelled = true;
    };
  }, [recipientId, donorId]);

  return {
    hlaScore,
    sabScore,
    epletScore,
    epletRiskTier,
    epletAnalysis,
    compositeScore,
    loading,
    error,
    isIncompatible,
    incompatibleReason,
  };
};

// ---- Component that renders a single exchange score for one R←D direction ----

export const ExchangeScore = ({ recipientId, donorId, label = "", size = "sm" }) => {
  const { compositeScore, hlaScore, sabScore, loading } = useMatchScore(recipientId, donorId);
  const pct = scoreToPercent(compositeScore);
  const colors = getScoreColor(pct);

  return (
    <div className={`p-2 rounded border ${loading ? "bg-gray-50 border-gray-200" : `${colors.bg} border-transparent`}`}>
      {label && (
        <p className="text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide">{label}</p>
      )}
      {loading ? (
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
          <span className="text-[11px] text-gray-400">Analyzing...</span>
        </div>
      ) : pct !== null ? (
        <div className="flex items-center gap-2">
          <SuccessBadge score={compositeScore} size={size} showLabel={false} />
          <div className="flex flex-col min-w-0">
            <span className={`text-xs font-bold ${colors.text}`}>{pct}%</span>
            <ScoreBar score={compositeScore} className="w-16" />
            <div className="flex gap-2 text-[9px] text-gray-400 mt-0.5">
              <span>HLA: {hlaScore !== null ? `${Math.round(hlaScore)}%` : "—"}</span>
              <span>SAB: {sabScore !== null ? `${Math.round(sabScore)}%` : "—"}</span>
            </div>
          </div>
        </div>
      ) : (
        <span className="text-[11px] text-gray-400">No data</span>
      )}
    </div>
  );
};
